import { DateTime } from 'luxon';

import {
  calendarItemsForDate,
  occurrenceStubForDate,
  type CalendarStreamItem,
} from '@/lib/calendar-stream';
import type { CheckinOccurrenceRow } from '@/pages/attendance/checkin-occurrence-utils';

export type CheckinGatheringChoice = CheckinOccurrenceRow & {
  /** Set when a scheduled occurrence already exists; created on open when null. */
  occurrenceId: string | null;
};

interface OccurrenceRow extends CheckinOccurrenceRow {
  status: string;
}

export const calendarItemToGatheringChoice = (
  item: CalendarStreamItem,
  date: string,
  occurrence?: OccurrenceRow,
): CheckinGatheringChoice => {
  const stub = occurrenceStubForDate(item, date);

  if (occurrence) {
    return {
      ...occurrence,
      occurrenceId: occurrence.id,
      gpsAutoCheckInEnabled: item.gpsAutoCheckInEnabled === true,
    };
  }

  const startsAt = stub?.startsAt ?? item.programStartsAt ?? '';
  const endsAt = stub?.endsAt ?? item.programEndsAt ?? '';

  return {
    id: stub?.id ?? `pending:${item.programId}:${date}`,
    programId: item.programId,
    programName: item.programName,
    programKind: item.programKind,
    programStartsOn: item.programStartsOn,
    programEndsOn: item.programEndsOn,
    occurrenceDate: date,
    startsAt,
    endsAt,
    venueName: item.venueName,
    dayDetailId: null,
    gpsAutoCheckInEnabled: item.gpsAutoCheckInEnabled === true,
    // Calendar schedule stubs use synthetic ids (`schedule:…`); only persisted rows are UUIDs.
    occurrenceId: null,
  };
};

const resolveOccurrence = (
  item: CalendarStreamItem,
  date: string,
  occurrences: OccurrenceRow[],
): OccurrenceRow | undefined =>
  occurrences.find(
    (row) => row.programId === item.programId && row.occurrenceDate === date,
  );

/** Check-in listing window: one hour before start through one hour after end (church-local). */
const CHECKIN_EARLY_MINUTES = 60;
const CHECKIN_LATE_MINUTES = 60;

/** True when now falls in the scheduled service window (with early/late check-in allowance). */
export const isGatheringInScheduleWindow = (
  startsAt: string,
  endsAt: string,
  timezone: string,
  reference: DateTime = DateTime.now(),
): boolean => {
  const now = reference.setZone(timezone);

  if (startsAt) {
    const start = DateTime.fromISO(startsAt, { zone: 'utc' }).setZone(timezone);
    if (start.isValid && now < start.minus({ minutes: CHECKIN_EARLY_MINUTES })) {
      return false;
    }
  }

  if (endsAt) {
    const end = DateTime.fromISO(endsAt, { zone: 'utc' }).setZone(timezone);
    if (end.isValid && now > end.plus({ minutes: CHECKIN_LATE_MINUTES })) {
      return false;
    }
  }

  return true;
};

export const gatheringsFromCalendar = (input: {
  calendarItems: CalendarStreamItem[];
  occurrences: OccurrenceRow[];
  date: string;
  openOccurrenceIds: Set<string>;
  openProgramIds: Set<string>;
  filterByScheduleTime?: boolean;
  timezone?: string;
}): CheckinGatheringChoice[] => {
  const items = calendarItemsForDate(input.calendarItems, input.date).filter(
    (item) => item.isActive !== false,
  );

  const resolved: CheckinGatheringChoice[] = [];

  for (const item of items) {
    if (input.openProgramIds.has(item.programId)) continue;

    const occurrence = resolveOccurrence(item, input.date, input.occurrences);
    if (occurrence && input.openOccurrenceIds.has(occurrence.id)) continue;

    const choice = calendarItemToGatheringChoice(item, input.date, occurrence);
    if (
      input.filterByScheduleTime &&
      input.timezone &&
      !isGatheringInScheduleWindow(choice.startsAt, choice.endsAt, input.timezone)
    ) {
      continue;
    }

    resolved.push(choice);
  }

  return resolved.sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
  );
};
